<script setup lang="ts">
import { DialogModal } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useCreateCreatorReport } from '@/api'
import { BoxComponent, ButtonComponent, FlexComponent, InputComponent, TypographyComponent } from '@/ui-library'

const DEFAULT_QUERY = `Analyze the provided images and stats. Write 5 actionable recommendations for this creator to increase order value.

Cover factors such as:
Posting timing - does it affect performance?
Text placement within the post
Color usage and visual style
Product selection and category
Copy/text formulation and tone
Ad disclosure - are posts correctly labeled?
Brand type and fit
Rules:
Base every insight strictly on what's visible in the images and data
No ranked list - just insights
No filler or fluff, but omit nothing meaningful
Be as specific as possible
Output format (HTML with inline CSS):
Two-column layout
One column: image example
One column: insight description
Images alternate sides between recommendations`

const props = defineProps<{
  userId: number
}>()

const emits = defineEmits<{
  created: []
}>()

const isOpen = defineModel<boolean>('modelValue', { default: false })

const query = ref(DEFAULT_QUERY)
const numberOfImages = ref<number | undefined>(100)

const { createReport, isLoading } = useCreateCreatorReport()

const isSubmitDisabled = computed(() => !query.value.trim())

const onSubmit = async () => {
  if (isSubmitDisabled.value) {
    return
  }
  const success = await createReport(props.userId, query.value, numberOfImages.value)
  if (success) {
    emits('created')
  }
}
</script>

<template>
  <DialogModal v-model="isOpen" width="640" @before-close="() => (isOpen = false)">
    <template #header>
      <TypographyComponent variant="headline-medium">Generate report</TypographyComponent>
    </template>

    <template #default>
      <BoxComponent :mt="16" :mb="24">
        <FlexComponent direction="column" :gap="16">
          <InputComponent v-model="query" type="textarea" :rows="12" placeholder="Ask Claude about this creator…">
            <template #header>
              <TypographyComponent variant="title-medium">Query</TypographyComponent>
            </template>
          </InputComponent>

          <FlexComponent direction="column" :gap="8">
            <TypographyComponent variant="title-medium">Number of images</TypographyComponent>
            <el-input-number v-model="numberOfImages" :min="1" :max="200" :controls="true" placeholder="Optional" />
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>

      <FlexComponent :gap="8" justify="flex-end">
        <ButtonComponent variant="text" :is-disabled="isLoading" @click="() => (isOpen = false)">Cancel</ButtonComponent>
        <ButtonComponent variant="primary" :is-loading="isLoading" :is-disabled="isSubmitDisabled" @click="onSubmit">
          Generate report
        </ButtonComponent>
      </FlexComponent>
    </template>
  </DialogModal>
</template>
